import {app, authentication, nestedAppAuth} from "@microsoft/teams-js";
import {
    createNestablePublicClientApplication,
    createStandardPublicClientApplication,
    type IPublicClientApplication,
} from "@azure/msal-browser";
import {jwtDecode} from "jwt-decode";
import {filterMsalCacheEntries} from "./msalCacheHandoff.ts";

export type TeamsAdminAccessToken = { token: string; expiresOnTimestamp: number };

type SsoTokenClaims = {
    exp: number;
    ver: string;
    tid: string;
    preferred_username?: string;
    upn?: string;
};

type SsoToken = TeamsAdminAccessToken & { claims: SsoTokenClaims };

// Refetch the SSO token when it expires within 5 minutes.
const TOKEN_REFRESH_SKEW_MS = 5 * 60_000;

// Teams clients where Nested App Authentication is used (when Teams also recommends it). Others keep the popup.
const NAA_CLIENT_TYPES = new Set<string>(["desktop", "macos"]);

function toScopeArray(scopes: string | string[]): string[] {
    return (typeof scopes === "string" ? scopes.split(" ") : scopes).filter(scope => scope.length > 0);
}

function getErrorMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
}

/**
 * Teams admin tab credential: the Teams SSO token for the Teams service APIs (empty scopes),
 * and MSAL-acquired Graph tokens with a consent popup (non-empty scopes).
 * Uses Nested App Authentication (host-rendered consent) when Teams recommends it, otherwise
 * the standard MSAL client with the auth-start/auth-end popup pages.
 */
export class TeamsAdminCredential {
    private readonly clientId: string;
    private readonly initiateLoginEndpoint: string;
    private ssoToken?: SsoToken;
    private initPromise?: Promise<void>;
    private pca?: IPublicClientApplication;
    private loginHint?: string;
    private usesNestedAppAuth = false;

    constructor({clientId, initiateLoginEndpoint}: { clientId: string; initiateLoginEndpoint: string }) {
        this.clientId = clientId;
        this.initiateLoginEndpoint = initiateLoginEndpoint;
    }

    async getToken(scopes: string | string[]): Promise<TeamsAdminAccessToken> {
        const scopeArray = toScopeArray(scopes);
        if (scopeArray.length === 0) {
            const {token, expiresOnTimestamp} = await this.getSsoToken();
            return {token, expiresOnTimestamp};
        }

        await this.init();
        const pca = this.pca!;
        let result;
        try {
            result = await pca.acquireTokenSilent({
                scopes: scopeArray,
                account: pca.getAccount({username: this.loginHint}) ?? undefined,
            });
        } catch {
            try {
                result = await pca.ssoSilent({scopes: scopeArray, loginHint: this.loginHint});
            } catch {
                // isConsentError in useGraphClient.ts matches "login first".
                throw new Error("Failed to get access token silently, please login first");
            }
        }

        return {
            token: result.accessToken,
            expiresOnTimestamp: result.expiresOn?.getTime() ?? jwtDecode<{ exp: number }>(result.accessToken).exp * 1000,
        };
    }

    async login(scopes: string | string[]): Promise<void> {
        try {
            await app.initialize();
            await this.init();
            if (this.usesNestedAppAuth) {
                await this.pca!.acquireTokenPopup({scopes: toScopeArray(scopes), loginHint: this.loginHint});
                return;
            }
            const scopeString = toScopeArray(scopes).join(" ");
            const result = await authentication.authenticate({
                url: `${this.initiateLoginEndpoint}?clientId=${encodeURIComponent(this.clientId)}`
                    + `&scope=${encodeURIComponent(scopeString)}`
                    + `&loginHint=${encodeURIComponent(this.loginHint ?? "")}`,
                width: 600,
                height: 535,
            });
            const parsed = JSON.parse(result) as { sessionStorage?: Record<string, unknown> };
            for (const [key, value] of Object.entries(filterMsalCacheEntries(parsed.sessionStorage ?? {}))) {
                sessionStorage.setItem(key, value);
            }
        } catch (error) {
            // Lowercase "consent": isConsentError in useGraphClient.ts matches it case-sensitively.
            throw new Error(`consent failed: ${getErrorMessage(error)}`);
        }
    }

    private init(): Promise<void> {
        this.initPromise ??= this.doInit().catch((error: unknown) => {
            this.initPromise = undefined;
            throw error;
        });
        return this.initPromise;
    }

    private async doInit(): Promise<void> {
        const {claims} = await this.getSsoToken();
        this.loginHint = claims.ver === "2.0" ? claims.preferred_username : claims.upn;
        const authority = `https://login.microsoftonline.com/${claims.tid}`;
        try {
            // createNestablePublicClientApplication silently returns a standard client when the host bridge
            // (installed by app.initialize() on NAA-capable hosts) is missing, so check for it explicitly.
            // NAA only on desktop. On iOS/Android it hands sign-in to Microsoft Authenticator, which dead-ends when
            // the user isn't signed in there; on web, Teams redirects the whole page for consent and comes back on
            // the app's default tab. Both keep the Teams popup (verified).
            const {app: {host: {clientType}}} = await app.getContext();
            if (NAA_CLIENT_TYPES.has(clientType)
                && nestedAppAuth.isNAAChannelRecommended()
                && "nestedAppAuthBridge" in window) {
                this.pca = await createNestablePublicClientApplication({auth: {clientId: this.clientId, authority}});
                this.usesNestedAppAuth = true;
                return;
            }
        } catch {
            // NAA unavailable: use the standard client and popup pages below.
        }
        this.pca = await createStandardPublicClientApplication({
            auth: {
                clientId: this.clientId,
                authority,
                redirectUri: `${window.location.origin}/blank-auth-end.html`,
            },
            cache: {cacheLocation: "sessionStorage"},
        });
    }

    private async getSsoToken(): Promise<SsoToken> {
        if (this.ssoToken && this.ssoToken.expiresOnTimestamp - Date.now() > TOKEN_REFRESH_SKEW_MS) {
            return this.ssoToken;
        }

        await app.initialize();
        const token = await authentication.getAuthToken();
        const claims = jwtDecode<SsoTokenClaims>(token);
        if (claims.ver !== "1.0" && claims.ver !== "2.0") {
            throw new Error(`Unsupported Teams SSO token version: ${claims.ver}`);
        }

        this.ssoToken = {token, expiresOnTimestamp: claims.exp * 1000, claims};
        return this.ssoToken;
    }
}
